"use client";

import React from "react";
import { Target, PenTool, Activity, ChevronRight, Sparkles, Laptop, Monitor } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const solutions = [{
  id: "ios",
  title: "iOS 移动端",
  description: "专为 iPhone 与 iPad 打造的沉浸式体验。支持离线下载、锁屏播放与 Apple Watch 进度同步，让碎片时间更有价值。",
  icon: Target,
  link: "https://apps.apple.com/cn/app/id6478130835"
}, {
  id: "android",
  title: "Android 移动端",
  description: "深度适配各大安卓机型，提供流畅的 AI 逐句拆解功能。内置智能记忆复习算法，随时随地开启高效精听训练。",
  icon: Activity,
  link: "https://app.listenmaster.cloud/download/listenmaster.apk"
}, {
  id: "macos",
  title: "macOS 桌面端",
  description: "专为 Mac 用户优化，支持全局快捷键导入音频，与系统通知深度整合，让听力练习无缝融入你的工作流。",
  icon: Laptop,
  link: "https://app.listenmaster.cloud/download/listenmaster.dmg"
}, {
  id: "windows",
  title: "Windows 桌面端",
  description: "高性能本地客户端，支持大文件快速处理与本地词库导出。完美适配分屏模式，边看边听，效率翻倍。",
  icon: Monitor,
  link: "https://app.listenmaster.cloud/download/listenmaster.exe"
}, {
  id: "web",
  title: "Web 网页版",
  description: "无需安装，在浏览器中即点即用。支持大屏双语对照模式，完美契合备考雅思、托福等长篇听力素材的深度练习场景。",
  icon: PenTool,
  link: "https://app.listenmaster.cloud/"
}];
export default function CoreGrowthSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="93606" id="选择你的平台" className="relative w-full bg-background py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[60%] rounded-full bg-secondary/10 blur-[120px]" />
        <div className="absolute top-[20%] left-[40%] w-[20%] h-[20%] rounded-full bg-accent/20 blur-[80px]" />
      </div>

      <div className="container mx-auto px-6 relative z-10 max-w-7xl">
        <div ref={ref} className={`flex flex-col items-center text-center mb-16 md:mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-bold mb-6">
            <Sparkles className="w-4 h-4" />
            <span>全平台同步学习</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-foreground tracking-tight mb-6">
            选择你的精听平台
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
            无论是在通勤路上还是书桌前，听力通都能为你提供最专业的 AI 听力支持，让每一句英语都听得清、记得牢。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {solutions.map((solution, i) => {
          const Icon = solution.icon;
          return <Card key={solution.id} data-index={i} className={`group relative flex flex-col h-full bg-card border-2 border-border/50 rounded-[24px] transition-all duration-500 ease-out hover:-translate-y-2.5 hover:border-secondary/60 hover:shadow-[0_20px_40px_rgba(28,176,246,0.15)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="absolute inset-0 rounded-[24px] bg-gradient-to-b from-secondary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <CardHeader className="pb-4 relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-secondary/10 border-2 border-secondary/20 flex items-center justify-center mb-6 text-secondary transition-all duration-500 group-hover:bg-secondary/20 group-hover:scale-110 group-hover:rotate-3">
                    <Icon className="w-8 h-8" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {solution.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="flex-grow relative z-10">
                  <CardDescription className="text-lg text-muted-foreground leading-relaxed font-medium">
                    {solution.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-6 relative z-10 mt-auto border-t border-border/50">
                  <Link to={solution.link} className="inline-flex items-center justify-center gap-2 text-base font-bold text-primary bg-primary/5 py-3 px-6 rounded-xl hover:bg-primary hover:text-white transition-all duration-300 w-full group/link">
                    {solution.id === "web" ? "立即体验" : "前往下载"}
                    <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </Link>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}